import actions from '@/store/pageBuilder/actions';

// ─── Helper functions ─────────────────────────────

function findBlockById(blocks, id) {
  return blocks.find((b) => b.id === id) || null;
}

function getBlockAndChildIds(blocks, parentId) {
  const ids = [parentId];
  const children = blocks.filter((b) => b.parentId === parentId);
  children.forEach((child) => {
    ids.push(...getBlockAndChildIds(blocks, child.id));
  });
  return ids;
}

const pageBuilderModule = {
  state: () => ({
    // Block tree
    blockTree: [],
    isBlockTreeLoaded: true,

    // Selection
    selectedBlockId: null,
    hoveredBlockId: null,

    // Clipboard
    clipboard: null,

    // Undo/Redo (Command Pattern)
    history: [],
    historyIndex: -1,
    maxHistorySize: 50,
    historyLabels: [], // Label for each history entry (e.g., 'Add block')

    // Dirty state
    isDirty: false,
    isSaving: false,

    // View
    viewMode: 'desktop', // 'desktop' | 'mobile'
    previewMode: false,
    zoom: 100,

    // Drag & Drop
    dragState: null,

    // Templates
    templates: [],
    isTemplatesLoaded: true,

    // Versions
    versions: [],
    isVersionsLoaded: true,

    // Subscription / Limits
    subscriptionLimits: {
      planType: 'free',
      maxPages: 5,
      maxBlocksPerPage: 20,
      customCssEnabled: false,
      customFontsEnabled: false,
    },

    // Page info
    currentPageId: null,
    currentCompanyId: null,
  }),

  getters: {
    canUndo(state) {
      return state.historyIndex > 0;
    },
    canRedo(state) {
      return state.historyIndex < state.history.length - 1;
    },
    currentHistoryLabel: (state) => {
      if (state.historyIndex >= 0 && state.historyIndex < state.historyLabels.length) {
        return state.historyLabels[state.historyIndex];
      }
      return '';
    },
    selectedBlock(state) {
      if (!state.selectedBlockId) return null;
      return findBlockById(state.blockTree, state.selectedBlockId);
    },
    rootBlocks(state) {
      return state.blockTree.filter((b) => !b.parentId);
    },
    blockCount(state) {
      return state.blockTree.length;
    },
    canAddBlock(state) {
      return state.blockTree.length < state.subscriptionLimits.maxBlocksPerPage;
    },
    isFreePlan(state) {
      return state.subscriptionLimits.planType === 'free';
    },
  },

  mutations: {
    // Block tree
    SET_BLOCK_TREE(state, blocks) {
      state.blockTree = blocks;
      state.isBlockTreeLoaded = true;
    },
    SET_BLOCK_TREE_LOADING(state) {
      state.isBlockTreeLoaded = false;
    },
    SET_BLOCK_TREE_ERROR(state) {
      state.isBlockTreeLoaded = true;
    },

    // Selection
    SET_SELECTED_BLOCK(state, blockId) {
      state.selectedBlockId = blockId;
    },
    SET_HOVERED_BLOCK(state, blockId) {
      state.hoveredBlockId = blockId;
    },
    CLEAR_SELECTION(state) {
      state.selectedBlockId = null;
      state.hoveredBlockId = null;
    },

    // Block CRUD
    ADD_BLOCK(state, { block, index }) {
      if (index !== undefined) {
        state.blockTree.splice(index, 0, block);
      } else {
        state.blockTree.push(block);
      }
      state.isDirty = true;
    },
    UPDATE_BLOCK(state, { blockId, changes }) {
      const block = findBlockById(state.blockTree, blockId);
      if (block) {
        Object.assign(block, changes);
        state.isDirty = true;
      }
    },
    REMOVE_BLOCK(state, blockId) {
      // Remove block and all children
      const idsToRemove = getBlockAndChildIds(state.blockTree, blockId);
      state.blockTree = state.blockTree.filter((b) => !idsToRemove.includes(b.id));
      if (state.selectedBlockId && idsToRemove.includes(state.selectedBlockId)) {
        state.selectedBlockId = null;
      }
      state.isDirty = true;
    },
    REORDER_BLOCKS(state, updatedBlocks) {
      updatedBlocks.forEach((update) => {
        const block = findBlockById(state.blockTree, update.id);
        if (block) {
          block.sortOrder = update.sortOrder;
          if (update.parentId !== undefined) {
            block.parentId = update.parentId;
          }
        }
      });
      state.isDirty = true;
    },
    SET_FULL_BLOCK_TREE(state, blocks) {
      state.blockTree = blocks;
      state.isDirty = true;
    },

    // History (Undo/Redo)
    PUSH_HISTORY(state, label) {
      // Remove any future states if we're in the middle of history
      if (state.historyIndex < state.history.length - 1) {
        state.history = state.history.slice(0, state.historyIndex + 1);
        state.historyLabels = state.historyLabels.slice(0, state.historyIndex + 1);
      }
      // Push current state
      state.history.push(JSON.parse(JSON.stringify(state.blockTree)));
      state.historyLabels.push(label || 'Edit');
      // Trim if exceeds max size
      if (state.history.length > state.maxHistorySize) {
        state.history.shift();
        state.historyLabels.shift();
      }
      state.historyIndex = state.history.length - 1;
    },
    UNDO(state) {
      if (state.historyIndex > 0) {
        state.historyIndex -= 1;
        state.blockTree = JSON.parse(JSON.stringify(state.history[state.historyIndex]));
        state.isDirty = true;
      }
    },
    REDO(state) {
      if (state.historyIndex < state.history.length - 1) {
        state.historyIndex += 1;
        state.blockTree = JSON.parse(JSON.stringify(state.history[state.historyIndex]));
        state.isDirty = true;
      }
    },

    // Clipboard
    SET_CLIPBOARD(state, block) {
      state.clipboard = JSON.parse(JSON.stringify(block));
    },
    CLEAR_CLIPBOARD(state) {
      state.clipboard = null;
    },

    // View
    SET_VIEW_MODE(state, mode) {
      state.viewMode = mode;
    },
    SET_PREVIEW_MODE(state, isPreview) {
      state.previewMode = isPreview;
    },
    SET_ZOOM(state, zoom) {
      state.zoom = zoom;
    },

    // Dirty / Saving
    SET_DIRTY(state, isDirty) {
      state.isDirty = isDirty;
    },
    SET_SAVING(state, isSaving) {
      state.isSaving = isSaving;
    },

    // Drag
    SET_DRAG_STATE(state, dragState) {
      state.dragState = dragState;
    },

    // Templates
    SET_TEMPLATES(state, templates) {
      state.templates = templates;
      state.isTemplatesLoaded = true;
    },
    SET_TEMPLATES_LOADING(state) {
      state.isTemplatesLoaded = false;
    },

    // Versions
    SET_VERSIONS(state, versions) {
      state.versions = versions;
      state.isVersionsLoaded = true;
    },
    SET_VERSIONS_LOADING(state) {
      state.isVersionsLoaded = false;
    },

    // Subscription limits
    SET_SUBSCRIPTION_LIMITS(state, limits) {
      state.subscriptionLimits = limits;
    },

    // Page context
    SET_PAGE_CONTEXT(state, { pageId, companyId }) {
      state.currentPageId = pageId;
      state.currentCompanyId = companyId;
    },

    // Reset
    RESET_BUILDER(state) {
      state.blockTree = [];
      state.selectedBlockId = null;
      state.hoveredBlockId = null;
      state.clipboard = null;
      state.history = [];
      state.historyIndex = -1;
      state.historyLabels = [];
      state.isDirty = false;
      state.isSaving = false;
      state.previewMode = false;
      state.dragState = null;
      state.versions = [];
      state.currentPageId = null;
    },
  },

  actions,
  namespaced: true,
};

export default pageBuilderModule;
